// Register.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes, css, Global } from '@emotion/react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const REGISTRATION_REQUEST_TIMEOUT_MS = Number(process.env.REACT_APP_REGISTRATION_REQUEST_TIMEOUT_MS) || 300000;
const SLOW_REGISTRATION_NOTICE_MS = 15000;

const getRegistrationErrorMessage = (error) => {
  if (error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('timeout')) {
    return 'Your application upload took longer than expected. Your documents may still have reached us, so please wait a minute and try signing in before submitting again.';
  }

  if (!error.response && error.request) {
    return 'We could not confirm your application submission because the network connection dropped. Please keep your connection on and try again if you do not receive confirmation.';
  }

  return error.response?.data?.error || 'Registration failed. Please try again.';
};

/* ════════════════════════════════════════════════════════════════════
   DESIGN TOKENS — shared with HomePage (professional/institutional)
   ════════════════════════════════════════════════════════════════════ */
const t = {
  c: {
    primary: '#0001FC',
    primaryDeep: '#0000B8',
    primaryLight: '#3A3AFF',
    primarySoft: '#E8E8FF',
    primaryFaint: '#F4F4FF',
    gradStart: '#0001FC',
    gradMid:   '#2828D8',
    gradEnd:   '#5050E0',
    ink: '#0F1729',
    inkSoft: '#1E2A47',
    text: '#1A2332',
    textMuted: '#5C6478',
    textLight: '#8C92A3',
    border: '#E5E8EE',
    borderSoft: '#EEF0F4',
    bg: '#FAFAFB',
    bgAlt: '#F4F6FA',
    surface: '#FFFFFF',
    accent: '#FFB547',
    accentLight: '#A5A5FF',
    success: '#1FA67F',
    successSoft: '#E0F5EC',
    warning: '#D98E2B',
    warningSoft: '#FAEBD3',
    danger: '#D0523E',
    dangerSoft: '#FBE7E2',
  },
  shadow: {
    xs: '0 1px 2px rgba(15, 23, 41, 0.04)',
    sm: '0 2px 8px rgba(15, 23, 41, 0.06)',
    md: '0 8px 24px rgba(15, 23, 41, 0.08)',
    lg: '0 18px 48px rgba(15, 23, 41, 0.10)',
    xl: '0 32px 80px rgba(15, 23, 41, 0.14)',
    blueGlow: '0 16px 40px -16px rgba(0, 1, 252, 0.45)',
  },
  radius: {
    xs: '4px', sm: '8px', md: '10px', lg: '14px', xl: '18px', pill: '999px',
  },
  ease: {
    out: 'cubic-bezier(0.22, 1, 0.36, 1)',
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  bp: { xs: '380px', sm: '600px', md: '900px', lg: '1200px' },
};

/* ════════════════════════════════════════════════════════════════════
   GLOBAL STYLES
   ════════════════════════════════════════════════════════════════════ */
const globalStyles = css`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; }
  html {
    scroll-behavior: smooth;
    -webkit-text-size-adjust: 100%;
  }
  body {
    margin: 0;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: ${t.c.text};
    background: ${t.c.bg};
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    overflow-x: hidden;
  }
  *::selection { background: ${t.c.primary}; color: #fff; }
  input, textarea, select { font-size: 16px; } /* prevents iOS zoom */
`;

/* ════════════════════════════════════════════════════════════════════
   ANIMATIONS
   ════════════════════════════════════════════════════════════════════ */
const riseIn = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const ripple = keyframes`
  0%   { transform: scale(1);   opacity: 0.5; }
  100% { transform: scale(2.4); opacity: 0; }
`;
const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;
/* ════════════════════════════════════════════════════════════════════
   ICONS — professional stroke icons
   ════════════════════════════════════════════════════════════════════ */
const IconUser = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.7" />
    <path d="M4 21 c0 -4 4 -7 8 -7 s8 3 8 7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
const IconMail = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
    <path d="M3.5 7 L12 13 L20.5 7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconPhone = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 4 h4 l2 5 -2.5 1.5 a11 11 0 0 0 5 5 L15 13 l5 2 v4 a2 2 0 0 1 -2 2 A16 16 0 0 1 3 6 a2 2 0 0 1 2 -2 z"
      stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
  </svg>
);
const IconLock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4" y="10" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.7" />
    <path d="M8 10 V7 a4 4 0 0 1 8 0 V10" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);
const IconBadge = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4" y="4" width="16" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
    <path d="M8 9 h8 M8 12 h8 M8 15 h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
const IconCalendar = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.7" />
    <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="1.7" />
    <line x1="8" y1="3" x2="8" y2="7"  stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <line x1="16" y1="3" x2="16" y2="7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
const IconGlobe = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
    <ellipse cx="12" cy="12" rx="4" ry="9" stroke="currentColor" strokeWidth="1.5" />
    <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);
const IconBuilding = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4" y="4" width="16" height="17" rx="1.5" stroke="currentColor" strokeWidth="1.7" />
    <line x1="9" y1="8"  x2="9" y2="8"   stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="15" y1="8" x2="15" y2="8"  stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="9" y1="12" x2="9" y2="12"  stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="15" y1="12" x2="15" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <rect x="10" y="16" width="4" height="5" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);
const IconMapPin = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 22 s8 -8 8 -13 a8 8 0 0 0 -16 0 c0 5 8 13 8 13 z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);
const IconClock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
    <polyline points="12 7 12 12 16 14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);
const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    <polyline points="12 5 19 12 12 19" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);
const IconCheck = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <polyline points="5 12.5 10 17 19 7"
      stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"
      fill="none" />
  </svg>
);
const IconAlertTri = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3 L22 20 H2 Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" fill="none" />
    <line x1="12" y1="10" x2="12" y2="14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="17" r="1" fill="currentColor" />
  </svg>
);
const IconLoader = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    style={{ animation: `${spin} 0.8s linear infinite` }} aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.4"
      strokeLinecap="round" strokeDasharray="26 54" opacity="0.9" />
  </svg>
);
const IconShield = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <polyline points="9 12 11 14 15 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);
const IconDocument = () => (
  <svg width="32" height="32" viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <rect x="4" y="4" width="40" height="40" rx="10" fill={t.c.primaryFaint} />
    <path d="M16 12 h12 l6 6 v18 a2 2 0 0 1 -2 2 H16 a2 2 0 0 1 -2 -2 V14 a2 2 0 0 1 2 -2 z"
      fill="white" stroke={t.c.primary} strokeWidth="1.7" strokeLinejoin="round" />
    <path d="M28 12 v6 h6" stroke={t.c.primary} strokeWidth="1.7" strokeLinejoin="round" fill="none" />
    <line x1="18" y1="24" x2="30" y2="24" stroke={t.c.primary} strokeWidth="1.7" strokeLinecap="round" />
    <line x1="18" y1="28" x2="30" y2="28" stroke={t.c.primary} strokeWidth="1.7" strokeLinecap="round" />
    <line x1="18" y1="32" x2="26" y2="32" stroke={t.c.primary} strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
const IconId = () => (
  <svg width="32" height="32" viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <rect x="4" y="4" width="40" height="40" rx="10" fill={t.c.primaryFaint} />
    <rect x="10" y="14" width="28" height="20" rx="2.5" fill="white" stroke={t.c.primary} strokeWidth="1.7" />
    <circle cx="19" cy="23" r="3" stroke={t.c.primary} strokeWidth="1.7" fill="none" />
    <path d="M15 31 c0 -2 2 -4 4 -4 s4 2 4 4" stroke={t.c.primary} strokeWidth="1.7" strokeLinecap="round" fill="none" />
    <line x1="28" y1="21" x2="34" y2="21" stroke={t.c.primary} strokeWidth="1.7" strokeLinecap="round" />
    <line x1="28" y1="25" x2="34" y2="25" stroke={t.c.primary} strokeWidth="1.7" strokeLinecap="round" />
    <line x1="28" y1="29" x2="32" y2="29" stroke={t.c.primary} strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
const IconCamera = () => (
  <svg width="32" height="32" viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <rect x="4" y="4" width="40" height="40" rx="10" fill={t.c.primaryFaint} />
    <rect x="10" y="16" width="28" height="18" rx="2.5" fill="white" stroke={t.c.primary} strokeWidth="1.7" />
    <path d="M18 16 l2 -3 h8 l2 3" stroke={t.c.primary} strokeWidth="1.7" strokeLinejoin="round" fill="none" />
    <circle cx="24" cy="25" r="5" stroke={t.c.primary} strokeWidth="1.7" fill="none" />
    <circle cx="24" cy="25" r="2" fill={t.c.primary} />
  </svg>
);
const IconX = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const IconCheckLarge = () => (
  <svg width="80" height="80" viewBox="0 0 100 100" fill="none" aria-hidden="true">
    <circle cx="50" cy="50" r="46" fill={t.c.primarySoft}>
      <animate attributeName="r" values="44;48;44" dur="2.4s" repeatCount="indefinite" />
    </circle>
    <circle cx="50" cy="50" r="34" fill={t.c.primary} />
    <path d="M34 52 L44 62 L66 40"
      stroke="white" strokeWidth="5.5" strokeLinecap="round" strokeLinejoin="round"
      fill="none" strokeDasharray="50" strokeDashoffset="50">
      <animate attributeName="stroke-dashoffset" values="50;0" dur="0.5s" begin="0.2s" fill="freeze" />
    </path>
  </svg>
);

/* ════════════════════════════════════════════════════════════════════
   STYLED COMPONENTS
   ════════════════════════════════════════════════════════════════════ */

const Page = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  background: ${t.c.bg};
  position: relative;
  overflow-x: hidden;

  /* Subtle ambient blue tint at corners */
  &::before {
    content: '';
    position: fixed;
    inset: 0;
    background:
      radial-gradient(ellipse 50% 35% at 85% 0%, ${t.c.primarySoft}, transparent 65%),
      radial-gradient(ellipse 40% 35% at 0% 100%, ${t.c.primaryFaint}, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }
`;

/* ──────── Logo mark ──────── */
const LogoFrame = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${t.radius.sm};
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 1, 252, 0.18);
  flex-shrink: 0;
  width: ${p => p.$size || 40}px;
  height: ${p => p.$size || 40}px;
  padding: 4px;

  img {
    width: 100%; height: 100%;
    object-fit: contain;
    display: block;
  }
`;
const LogoMark = ({ size = 40 }) => (
  <LogoFrame $size={size}>
    <img src="/logo192.png" alt="Visioning Assessment" />
  </LogoFrame>
);

/* ──────── Top bar (sticky) ──────── */
const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 30;
  background: rgba(250, 250, 251, 0.92);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  border-bottom: 1px solid ${t.c.border};
`;

const TopInner = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 14px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: ${t.bp.sm}) { padding: 12px 16px; }
`;

const TopBrand = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: ${t.c.ink};

  .wordmark {
    line-height: 1.1;
    .name {
      font-weight: 700;
      font-size: 1rem;
      letter-spacing: -0.01em;
    }
    .sub {
      display: block;
      font-size: 0.66rem;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: ${t.c.textMuted};
      margin-top: 2px;
      font-weight: 500;
    }
  }

  @media (max-width: ${t.bp.sm}) {
    .wordmark .sub { display: none; }
  }
`;

const TopSecure = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${t.c.successSoft};
  border: 1px solid ${t.c.success}30;
  border-radius: ${t.radius.pill};
  font-size: 0.7rem;
  font-weight: 600;
  color: ${t.c.success};

  .dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: ${t.c.success};
    position: relative;
    &::after {
      content: '';
      position: absolute;
      inset: -2px;
      border-radius: 50%;
      background: ${t.c.success};
      animation: ${ripple} 2s ${t.ease.out} infinite;
    }
  }

  @media (max-width: ${t.bp.xs}) {
    padding: 5px 10px;
    font-size: 0.65rem;
  }
`;

/* ──────── Main shell ──────── */
const Shell = styled.main`
  position: relative;
  z-index: 1;
  max-width: 860px;
  margin: 0 auto;
  padding: 40px 24px 72px;

  @media (max-width: ${t.bp.md}) { padding: 28px 18px 56px; }
  @media (max-width: ${t.bp.sm}) { padding: 20px 14px 48px; }
`;

/* ──────── Intro ──────── */
const Intro = styled.section`
  text-align: center;
  margin-bottom: 32px;
  animation: ${riseIn} 600ms ${t.ease.out} both;

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    background: ${t.c.surface};
    border: 1px solid ${t.c.border};
    border-radius: ${t.radius.pill};
    font-size: 0.72rem;
    font-weight: 600;
    color: ${t.c.primary};
    box-shadow: ${t.shadow.xs};
    margin-bottom: 16px;
    letter-spacing: 0.06em;
    text-transform: uppercase;

    .dot {
      width: 6px; height: 6px;
      border-radius: 50%;
      background: ${t.c.primary};
    }
  }

  h1 {
    font-weight: 800;
    font-size: clamp(1.7rem, 4.2vw, 2.4rem);
    letter-spacing: -0.025em;
    line-height: 1.15;
    margin: 0 0 12px;
    color: ${t.c.ink};

    .accent { color: ${t.c.primary}; }
  }

  p {
    font-size: 1rem;
    color: ${t.c.textMuted};
    max-width: 540px;
    margin: 0 auto;
    line-height: 1.6;
  }

  @media (max-width: ${t.bp.sm}) {
    margin-bottom: 24px;
    p { font-size: 0.9rem; }
  }
`;

/* ──────── Step rail ──────── */
const Rail = styled.nav`
  background: ${t.c.surface};
  border: 1px solid ${t.c.border};
  border-radius: ${t.radius.lg};
  padding: 18px 20px 14px;
  margin-bottom: 20px;
  box-shadow: ${t.shadow.sm};
  animation: ${riseIn} 600ms ${t.ease.out} 100ms both;

  @media (max-width: ${t.bp.sm}) {
    padding: 14px 14px 12px;
    border-radius: ${t.radius.md};
  }
`;

const RailTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 12px;
  gap: 12px;

  .meta {
    font-size: 0.7rem;
    color: ${t.c.textMuted};
    letter-spacing: 0.12em;
    text-transform: uppercase;
    font-weight: 600;
  }
  .count {
    font-size: 0.85rem;
    font-weight: 700;
    color: ${t.c.primary};
    font-variant-numeric: tabular-nums;
  }
`;

const RailSteps = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media (max-width: ${t.bp.sm}) { gap: 6px; }
`;

const RailStep = styled.li`
  position: relative;
  padding: 12px 14px;
  border-radius: ${t.radius.md};
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${p => p.$active
    ? t.c.primaryFaint
    : p.$completed
      ? t.c.primarySoft
      : t.c.bgAlt};
  border: 1px solid ${p => p.$active
    ? t.c.primary
    : p.$completed
      ? t.c.primary + '50'
      : t.c.border};
  transition: all 260ms ${t.ease.out};

  ${p => p.$active && css`
    box-shadow: 0 0 0 3px ${t.c.primarySoft};
  `}

  .dot {
    width: 26px; height: 26px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-weight: 700;
    font-size: 0.78rem;
    background: ${p => p.$active ? t.c.primary : p.$completed ? t.c.primary : t.c.surface};
    color: ${p => p.$active ? '#fff' : p.$completed ? '#fff' : t.c.textMuted};
    border: 1px solid ${p => p.$active ? t.c.primary : p.$completed ? t.c.primary : t.c.border};
    transition: all 260ms;
  }

  .text {
    display: flex;
    flex-direction: column;
    line-height: 1.15;
    min-width: 0;

    .label {
      font-size: 0.84rem;
      font-weight: 600;
      color: ${p => p.$active ? t.c.ink : p.$completed ? t.c.primaryDeep : t.c.text};
      letter-spacing: -0.005em;
    }
    .sub {
      font-size: 0.7rem;
      color: ${t.c.textMuted};
      margin-top: 2px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  @media (max-width: ${t.bp.md}) {
    padding: 10px 10px;
    .text .sub { display: none; }
    .text .label { font-size: 0.78rem; }
  }
  @media (max-width: ${t.bp.sm}) {
    padding: 10px 8px;
    gap: 8px;
    .dot { width: 22px; height: 22px; font-size: 0.72rem; }
    .text .label { font-size: 0.72rem; }
  }
`;

const RailBar = styled.div`
  margin-top: 14px;
  height: 4px;
  background: ${t.c.borderSoft};
  border-radius: ${t.radius.pill};
  overflow: hidden;

  .fill {
    height: 100%;
    width: ${p => p.$percent}%;
    background: linear-gradient(90deg, ${t.c.primary}, ${t.c.primaryLight});
    border-radius: ${t.radius.pill};
    transition: width 500ms ${t.ease.out};
  }
`;

/* ──────── Card ──────── */
const Card = styled.section`
  background: ${t.c.surface};
  border: 1px solid ${t.c.border};
  border-radius: ${t.radius.xl};
  padding: 36px;
  position: relative;
  overflow: hidden;
  box-shadow: ${t.shadow.lg};
  animation: ${riseIn} 500ms ${t.ease.out} both;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${t.c.primary}, ${t.c.primaryLight});
  }

  @media (max-width: ${t.bp.md}) {
    padding: 28px 22px;
    border-radius: ${t.radius.lg};
  }
  @media (max-width: ${t.bp.sm}) {
    padding: 22px 16px;
  }
`;

const CardHeader = styled.div`
  margin-bottom: 24px;

  .pill {
    display: inline-block;
    padding: 4px 10px;
    background: ${t.c.primaryFaint};
    color: ${t.c.primary};
    border-radius: ${t.radius.sm};
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin-bottom: 10px;
  }

  h2 {
    font-weight: 800;
    font-size: clamp(1.3rem, 3.5vw, 1.7rem);
    letter-spacing: -0.02em;
    line-height: 1.15;
    color: ${t.c.ink};
    margin: 0 0 6px;
  }
  p {
    font-size: 0.94rem;
    color: ${t.c.textMuted};
    margin: 0;
    line-height: 1.55;
  }
`;

/* ──────── Form grid & fields ──────── */
const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const Field = styled.div`
  margin-bottom: 16px;
  animation: ${riseIn} 400ms ${t.ease.out} ${p => p.$delay || '0ms'} both;
`;

const LabelRow = styled.label`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 7px;
  font-size: 0.84rem;
  font-weight: 600;
  color: ${t.c.text};
  letter-spacing: -0.005em;

  .req { color: ${t.c.danger}; font-weight: 700; }
  .hint {
    font-weight: 400;
    font-size: 0.74rem;
    color: ${t.c.textMuted};
  }
`;

const InputShell = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  /* Every input/select/textarea inside a shell that has an icon gets
     left padding to clear the 18px icon at left:16px. This sidesteps
     any prop-plumbing fragility. */
  input, select, textarea {
    padding-left: 50px;
  }

  &:focus-within .field-ico { color: ${t.c.primary}; }
`;

const FieldIco = styled.span`
  position: absolute;
  left: 16px;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  color: ${t.c.textLight};
  transition: color 220ms;
  pointer-events: none;
  z-index: 1;
`;

const inputBase = css`
  width: 100%;
  height: 50px;
  padding: 0 14px;
  background: ${t.c.surface};
  border: 1.5px solid ${t.c.border};
  border-radius: ${t.radius.md};
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  color: ${t.c.ink};
  outline: none;
  transition: all 180ms;
  box-sizing: border-box;
  -webkit-appearance: none;

  &::placeholder { color: ${t.c.textLight}; font-weight: 400; }

  &:hover:not(:focus):not(:disabled) {
    border-color: ${t.c.textLight};
  }
  &:focus {
    border-color: ${t.c.primary};
    box-shadow: 0 0 0 4px ${t.c.primarySoft};
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }

  &:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 1000px ${t.c.surface} inset;
    -webkit-text-fill-color: ${t.c.ink};
  }

  @media (max-width: ${t.bp.sm}) { height: 48px; }
`;

const Input = styled.input`
  ${inputBase}
  ${p => p.$error && css`
    border-color: ${t.c.danger} !important;
    background: ${t.c.dangerSoft}40 !important;
  `}
`;

const Select = styled.select`
  ${inputBase}
  padding-right: 38px;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%230001FC' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round' d='M1 2l5 5 5-5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;

  ${p => p.$error && css`
    border-color: ${t.c.danger} !important;
    background-color: ${t.c.dangerSoft}40 !important;
  `}
  option { color: ${t.c.ink}; background: ${t.c.surface}; }
`;

const TextArea = styled.textarea`
  ${inputBase}
  min-height: 110px;
  height: auto;
  padding: 12px 14px;
  line-height: 1.55;
  resize: vertical;

  ${p => p.$error && css`
    border-color: ${t.c.danger} !important;
    background: ${t.c.dangerSoft}40 !important;
  `}
`;

const ErrText = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 7px;
  color: ${t.c.danger};
  font-size: 0.78rem;
  font-weight: 500;

  svg { flex-shrink: 0; }
`;

/* ──────── Divider ──────── */
const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 24px 0 18px;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${t.c.border};
  }
  span {
    font-size: 0.72rem;
    font-weight: 700;
    color: ${t.c.textMuted};
    white-space: nowrap;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
`;

/* ──────── File upload ──────── */
const DropZone = styled.div`
  border: 1.5px dashed ${p => p.$hasFile ? t.c.primary : p.$error ? t.c.danger : t.c.border};
  border-radius: ${t.radius.lg};
  padding: 24px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 240ms ${t.ease.out};
  background: ${p => p.$hasFile ? t.c.primaryFaint : t.c.bg};
  position: relative;
  -webkit-tap-highlight-color: transparent;

  &:hover, &:active {
    border-color: ${t.c.primary};
    background: ${p => p.$hasFile ? t.c.primarySoft : t.c.primaryFaint};
    transform: translateY(-1px);
  }

  @media (max-width: ${t.bp.sm}) {
    padding: 20px 14px;
  }
`;

const DropIcon = styled.div`
  margin: 0 auto 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const DropTitle = styled.div`
  font-weight: 700;
  font-size: 0.95rem;
  color: ${t.c.ink};
  margin-bottom: 4px;
  letter-spacing: -0.005em;
`;

const DropSub = styled.div`
  font-size: 0.8rem;
  color: ${t.c.textMuted};
`;

const FileBadge = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  background: ${t.c.surface};
  border: 1px solid ${t.c.primary}30;
  border-radius: ${t.radius.md};
  text-align: left;
  display: flex;
  align-items: center;
  gap: 10px;

  .ico {
    flex-shrink: 0;
    color: ${t.c.primary};
    display: inline-flex;
  }
  .meta {
    flex: 1;
    min-width: 0;
    .n {
      font-size: 0.85rem;
      font-weight: 600;
      color: ${t.c.ink};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .s {
      font-size: 0.74rem;
      color: ${t.c.textMuted};
      margin-top: 2px;
    }
  }
`;

const RemoveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 11px;
  background: ${t.c.dangerSoft};
  color: ${t.c.danger};
  border: 1px solid ${t.c.danger}30;
  border-radius: ${t.radius.sm};
  font-family: inherit;
  font-size: 0.74rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 180ms;
  -webkit-tap-highlight-color: transparent;
  flex-shrink: 0;
  min-height: 32px;

  &:hover { background: ${t.c.danger}; color: #fff; }
`;

const PhotoPreview = styled.div`
  margin: 14px auto 0;
  width: 96px;
  height: 96px;
  border-radius: ${t.radius.md};
  overflow: hidden;
  border: 2px solid ${t.c.primary};
  box-shadow: ${t.shadow.md};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

/* ──────── Info callout ──────── */
const SubmissionNotice = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  margin: 18px 0 0;
  border-radius: ${t.radius.md};
  background: ${p => p.$type === 'error' ? t.c.dangerSoft : t.c.primaryFaint};
  border: 1px solid ${p => p.$type === 'error' ? `${t.c.danger}30` : `${t.c.primary}30`};
  border-left: 3px solid ${p => p.$type === 'error' ? t.c.danger : t.c.primary};
  animation: ${riseIn} 260ms ${t.ease.out} both;

  .ico {
    color: ${p => p.$type === 'error' ? t.c.danger : t.c.primary};
    flex-shrink: 0;
    padding-top: 2px;
  }

  .body {
    font-size: 0.86rem;
    line-height: 1.5;
    color: ${t.c.text};
  }

  strong {
    color: ${p => p.$type === 'error' ? t.c.danger : t.c.ink};
    display: block;
    font-size: 0.91rem;
    margin-bottom: 2px;
  }
`;

const Callout = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 18px;
  margin: 22px 0 4px;
  border-radius: ${t.radius.md};
  background: ${t.c.primaryFaint};
  border: 1px solid ${t.c.primary}30;
  border-left: 3px solid ${t.c.primary};

  .ico {
    color: ${t.c.primary};
    flex-shrink: 0;
    padding-top: 2px;
  }

  .body {
    font-size: 0.88rem;
    line-height: 1.55;
    color: ${t.c.text};

    strong {
      font-weight: 700;
      color: ${t.c.ink};
      font-size: 0.93rem;
      display: block;
      margin-bottom: 2px;
    }
  }
`;

/* ──────── Buttons ──────── */
const Actions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 28px;

  @media (max-width: ${t.bp.sm}) {
    flex-direction: column-reverse;
    gap: 8px;
  }
`;

const BtnBase = styled.button`
  height: 52px;
  padding: 0 24px;
  border-radius: ${t.radius.md};
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 220ms ${t.ease.out};
  -webkit-tap-highlight-color: transparent;
  border: 1.5px solid transparent;
  position: relative;
  overflow: hidden;

  &:disabled { cursor: not-allowed; opacity: 0.6; }
  & > * { position: relative; z-index: 1; }

  @media (max-width: ${t.bp.sm}) { height: 50px; width: 100%; }
`;

const BtnPrimary = styled(BtnBase)`
  background: ${t.c.primary};
  color: #fff;
  border-color: ${t.c.primary};
  flex: 1;

  &:hover:not(:disabled) {
    background: ${t.c.primaryDeep};
    border-color: ${t.c.primaryDeep};
    transform: translateY(-2px);
    box-shadow: ${t.shadow.blueGlow};
  }
  &:active:not(:disabled) { transform: translateY(0); }

  ${p => p.$loading && css`
    background: linear-gradient(90deg, ${t.c.primary}, ${t.c.primaryLight}, ${t.c.primary});
    background-size: 200% 100%;
    animation: ${shimmer} 1.6s linear infinite;
  `}
`;

const BtnGhost = styled(BtnBase)`
  background: ${t.c.surface};
  color: ${t.c.text};
  border-color: ${t.c.border};
  flex: 0.5;

  &:hover:not(:disabled) {
    border-color: ${t.c.ink};
    background: ${t.c.bgAlt};
  }

  @media (max-width: ${t.bp.sm}) { flex: none; }

  svg { transform: rotate(180deg); }
`;

/* ──────── Footer note ──────── */
const CardFoot = styled.div`
  text-align: center;
  margin-top: 28px;
  padding-top: 20px;
  border-top: 1px solid ${t.c.border};
  font-size: 0.88rem;
  color: ${t.c.textMuted};
  animation: ${riseIn} 500ms ${t.ease.out} 300ms both;

  a {
    color: ${t.c.primary};
    font-weight: 600;
    text-decoration: none;
    transition: color 180ms;
    &:hover { color: ${t.c.primaryDeep}; text-decoration: underline; }
  }
`;

/* ──────── Success screen ──────── */
const SuccessCard = styled.section`
  background: ${t.c.surface};
  border: 1px solid ${t.c.border};
  border-radius: ${t.radius.xl};
  padding: 56px 36px;
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: ${t.shadow.lg};
  animation: ${riseIn} 600ms ${t.ease.out} both;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${t.c.primary}, ${t.c.primaryLight});
  }

  .check { margin-bottom: 24px; display: inline-block; }

  .pill {
    display: inline-block;
    padding: 4px 12px;
    background: ${t.c.successSoft};
    color: ${t.c.success};
    border-radius: ${t.radius.sm};
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin-bottom: 14px;
  }

  h2 {
    font-weight: 800;
    font-size: clamp(1.6rem, 4.2vw, 2.1rem);
    letter-spacing: -0.025em;
    line-height: 1.15;
    color: ${t.c.ink};
    margin: 0 0 12px;
  }

  p {
    font-size: 1rem;
    color: ${t.c.textMuted};
    max-width: 460px;
    margin: 0 auto 16px;
    line-height: 1.65;
  }

  .email-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: ${t.c.primaryFaint};
    border: 1px solid ${t.c.primary}40;
    border-radius: ${t.radius.pill};
    color: ${t.c.primaryDeep};
    font-weight: 600;
    font-size: 0.9rem;
    margin: 12px 0 24px;
    max-width: 100%;
    word-break: break-all;
  }

  @media (max-width: ${t.bp.sm}) {
    padding: 40px 22px;
  }
`;

/* ════════════════════════════════════════════════════════════════════
   DATA
   ════════════════════════════════════════════════════════════════════ */
const LICENSE_TYPES = [
  'Licensed Psychologist',
  'Licensed Professional Counselor (LPC)',
  'Licensed Therapist (LT)',
  'Licensed Mental Health Officer (LMHO)',
  'Psychiatrist (MD)',
  'Psychiatric Nurse Practitioner',
  'Other',
];

const HIGHEST_EDUCATIONAL_LEVELS = [
  'Diploma/HND',
  "Bachelor's Degree",
  'MBChB',
  "Master's Degree",
  'Doctorate Degree',
];

const COUNTRY_FALLBACK = [
  'Argentina', 'Australia', 'Brazil', 'Canada', 'China', 'France',
  'Germany', 'Ghana', 'India', 'Ireland', 'Italy', 'Japan', 'Kenya',
  'Mexico', 'Netherlands', 'New Zealand', 'Nigeria', 'Norway',
  'Philippines', 'Portugal', 'Saudi Arabia', 'Singapore', 'South Africa',
  'Spain', 'Sweden', 'Switzerland', 'United Arab Emirates',
  'United Kingdom', 'United States',
];

const STEPS = [
  { id: 1, label: 'Account',     sub: 'Your details' },
  { id: 2, label: 'Credentials', sub: 'License & work' },
  { id: 3, label: 'Documents',   sub: 'Files & profile' },
];

/* ════════════════════════════════════════════════════════════════════
   COMPONENT
   ════════════════════════════════════════════════════════════════════ */
const Register = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [submissionNotice, setSubmissionNotice] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  const [countries, setCountries] = useState(COUNTRY_FALLBACK);
  const [countriesLoading, setCountriesLoading] = useState(false);

  const cvInputRef = useRef(null);
  const licenseInputRef = useRef(null);
  const photoInputRef = useRef(null);

  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '',
    firstName: '', lastName: '', phone: '',
    licenseNumber: '', licenseType: '', licenseTypeOther: '',
    licenseState: '', country: '',
    licenseExpiry: '', highestEducationalLevel: '', yearsOfExperience: '',
    workplace: '', workplaceAddress: '', bio: '',
    specializations: [],
  });

  const [cvFile, setCvFile] = useState(null);
  const [licenseFile, setLicenseFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  useEffect(() => {
    const fetchCountries = async () => {
      setCountriesLoading(true);
      try {
        const res = await fetch('https://restcountries.com/v3.1/all?fields=name');
        if (!res.ok) return;
        const data = await res.json();
        const names = data
          .map((item) => item?.name?.common)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        if (names.length > 0) setCountries(names);
      } catch {
        // keep fallback
      } finally {
        setCountriesLoading(false);
      }
    };
    fetchCountries();
  }, []);

  /* ── Handlers ── */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleCvChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('CV must be a PDF file');
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('CV must be less than 5MB');
      e.target.value = '';
      return;
    }
    setCvFile(file);
    setErrors((prev) => ({ ...prev, cvFile: null }));
    toast.success(`CV selected: ${file.name}`);
  };

  const handleLicenseChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = [
      'application/pdf', 'image/jpeg', 'image/jpg',
      'image/png', 'image/gif', 'image/webp',
    ];
    if (!validTypes.includes(file.type)) {
      toast.error('License must be a PDF or image file');
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('License file must be less than 5MB');
      e.target.value = '';
      return;
    }
    setLicenseFile(file);
    setErrors((prev) => ({ ...prev, licenseFile: null }));
    toast.success(`License selected: ${file.name}`);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      e.target.value = '';
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Photo must be less than 2MB');
      e.target.value = '';
      return;
    }
    setPhotoFile(file);
    setErrors((prev) => ({ ...prev, photoFile: null }));
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
    toast.success(`Photo selected: ${file.name}`);
  };

  const removeCv = (e) => {
    e.stopPropagation();
    setCvFile(null);
    if (cvInputRef.current) cvInputRef.current.value = '';
  };

  const removeLicense = (e) => {
    e.stopPropagation();
    setLicenseFile(null);
    if (licenseInputRef.current) licenseInputRef.current.value = '';
  };

  const removePhoto = (e) => {
    e.stopPropagation();
    setPhotoFile(null);
    setPhotoPreview(null);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  /* ── Validation ── */
  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim())  newErrors.lastName  = 'Last name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    if (step === 2) {
      if (!formData.licenseType) newErrors.licenseType = 'Please select your license type';
      if (formData.licenseType === 'Other' && !(formData.licenseTypeOther || '').trim()) {
        newErrors.licenseTypeOther = 'Please specify your license type';
      }
      if (!formData.licenseNumber.trim())    newErrors.licenseNumber    = 'License number is required';
      if (!formData.licenseState.trim())     newErrors.licenseState     = 'License state/region is required';
      if (!formData.country.trim())          newErrors.country          = 'Country is required';
      if (!formData.licenseExpiry.trim())    newErrors.licenseExpiry    = 'Expiry date is required';
      if (!formData.highestEducationalLevel) newErrors.highestEducationalLevel = 'Please select your highest educational level';
      if (!formData.yearsOfExperience.trim()) newErrors.yearsOfExperience = 'Years of experience is required';
      if (!formData.workplace.trim())        newErrors.workplace        = 'Current workplace is required';
      if (!formData.workplaceAddress.trim()) newErrors.workplaceAddress = 'Workplace address is required';
    }

    if (step === 3) {
      if (!photoFile) newErrors.photoFile = 'Profile photo is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => prev + 1);
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      toast.error('Please complete all required fields');
      return;
    }
    setLoading(true);
    setSubmissionNotice({
      type: 'info',
      title: 'Submitting your application',
      message: 'Uploading your details and documents securely. Please keep this page open.',
    });
    setUploadProgress(null);

    const slowSubmissionTimer = setTimeout(() => {
      setSubmissionNotice({
        type: 'info',
        title: 'Still submitting',
        message: 'Large files and slower networks can take a few minutes. Please keep this tab open; we are still waiting for the server response.',
      });
    }, SLOW_REGISTRATION_NOTICE_MS);

    try {
      const submitData = new FormData();
      submitData.append('email',             formData.email.trim().toLowerCase());
      submitData.append('password',          formData.password);
      submitData.append('firstName',         formData.firstName);
      submitData.append('lastName',          formData.lastName);
      submitData.append('phone',             formData.phone || '');
      submitData.append('licenseNumber',     formData.licenseNumber);
      submitData.append('licenseType',       formData.licenseType);
      submitData.append('licenseTypeOther',  formData.licenseTypeOther || '');
      submitData.append('licenseState',      formData.licenseState || '');
      submitData.append('country',           formData.country || '');
      submitData.append('licenseExpiry',     formData.licenseExpiry || '');
      submitData.append('yearsOfExperience', formData.yearsOfExperience || '0');
      submitData.append('workplace',         formData.workplace || '');
      submitData.append('workplaceAddress',  formData.workplaceAddress || '');
      submitData.append('bio',               formData.bio || '');
      submitData.append('specializations',   JSON.stringify(formData.specializations));
      submitData.append('education',         JSON.stringify([{ level: formData.highestEducationalLevel }]));
      if (cvFile)      submitData.append('cv',              cvFile);
      if (licenseFile) submitData.append('licenseDocument', licenseFile);
      if (photoFile)   submitData.append('profileImage',    photoFile);

      await axios.post(`${API_URL}/auth/practitioner/register`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: REGISTRATION_REQUEST_TIMEOUT_MS,
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          setUploadProgress(Math.min(100, Math.round((progressEvent.loaded * 100) / progressEvent.total)));
        },
      });

      setSuccess(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      const message = getRegistrationErrorMessage(error);
      setSubmissionNotice({ type: 'error', title: 'Submission not confirmed', message });
      toast.error(message);
    } finally {
      clearTimeout(slowSubmissionTimer);
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const progressPercent = (currentStep / STEPS.length) * 100;
  const fmtSize = (bytes) => (bytes / 1024 / 1024).toFixed(2) + ' MB';

  /* ════════════════ SUCCESS SCREEN ════════════════ */
  if (success) {
    return (
      <Page>
        <Global styles={globalStyles} />
        <TopBar>
          <TopInner>
            <TopBrand to="/">
              <LogoMark size={40} />
              <div className="wordmark">
                <span className="name">Visioning Assessment</span>
                <span className="sub">Practitioner Portal</span>
              </div>
            </TopBrand>
            <TopSecure>
              <span className="dot" />
              Secure
            </TopSecure>
          </TopInner>
        </TopBar>

        <Shell>
          <SuccessCard>
            <span className="check"><IconCheckLarge /></span>
            <span className="pill">Application Submitted</span>
            <h2>Thank you for applying.</h2>
            <p>
              Your practitioner application has been received and is now under review by our admin team.
            </p>
            <div className="email-pill">
              <IconMail />
              {formData.email}
            </div>
            <p>
              You'll receive an email once your account is approved — this typically takes 1–2 business days.
            </p>
            <div style={{ marginTop: 12 }}>
              <BtnPrimary
                type="button"
                onClick={() => navigate('/login')}
                style={{ minWidth: 180, maxWidth: 220, margin: '0 auto' }}
              >
                <span>Go to sign in</span>
                <IconArrow />
              </BtnPrimary>
            </div>
          </SuccessCard>
        </Shell>
      </Page>
    );
  }

  /* ════════════════ MAIN RENDER ════════════════ */
  return (
    <Page>
      <Global styles={globalStyles} />

      {/* Sticky top bar */}
      <TopBar>
        <TopInner>
          <TopBrand to="/">
            <LogoMark size={40} />
            <div className="wordmark">
              <span className="name">Visioning Assessment</span>
              <span className="sub">Practitioner Portal</span>
            </div>
          </TopBrand>
          <TopSecure>
            <span className="dot" />
            Secure signup
          </TopSecure>
        </TopInner>
      </TopBar>

      <Shell>
        {/* Intro */}
        <Intro>
          <div className="eyebrow">
            <span className="dot" />
            Practitioner Registration
          </div>
          <h1>Join our network of <span className="accent">verified practitioners</span>.</h1>
          <p>
            A short three-step application. We'll review your credentials and respond within 1–2 business days.
          </p>
        </Intro>

        {/* Step rail */}
        <Rail>
          <RailTop>
            <span className="meta">Application Progress</span>
            <span className="count">
              Step {String(currentStep).padStart(2, '0')} of {String(STEPS.length).padStart(2, '0')}
            </span>
          </RailTop>
          <RailSteps>
            {STEPS.map((s) => {
              const isActive = currentStep === s.id;
              const isCompleted = currentStep > s.id;
              return (
                <RailStep key={s.id} $active={isActive} $completed={isCompleted}>
                  <span className="dot">
                    {isCompleted ? <IconCheck size={14} /> : s.id}
                  </span>
                  <span className="text">
                    <span className="label">{s.label}</span>
                    <span className="sub">{s.sub}</span>
                  </span>
                </RailStep>
              );
            })}
          </RailSteps>
          <RailBar $percent={progressPercent}>
            <div className="fill" />
          </RailBar>
        </Rail>

        {/* Form card */}
        <Card>
          {/* ═════ STEP 1 ═════ */}
          {currentStep === 1 && (
            <>
              <CardHeader>
                <span className="pill">Step 01 — Account</span>
                <h2>Create your account.</h2>
                <p>Set up the credentials you'll use to sign in to the platform.</p>
              </CardHeader>

              <Grid2>
                <Field $delay="40ms">
                  <LabelRow>First name <span className="req">*</span></LabelRow>
                  <InputShell>
                    <FieldIco className="field-ico"><IconUser /></FieldIco>
                    <Input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="Jane"
                      autoComplete="given-name"
                      $withIcon
                      $error={errors.firstName}
                    />
                  </InputShell>
                  {errors.firstName && <ErrText><IconAlertTri />{errors.firstName}</ErrText>}
                </Field>

                <Field $delay="80ms">
                  <LabelRow>Last name <span className="req">*</span></LabelRow>
                  <InputShell>
                    <FieldIco className="field-ico"><IconUser /></FieldIco>
                    <Input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Doe"
                      autoComplete="family-name"
                      $withIcon
                      $error={errors.lastName}
                    />
                  </InputShell>
                  {errors.lastName && <ErrText><IconAlertTri />{errors.lastName}</ErrText>}
                </Field>
              </Grid2>

              <Field $delay="120ms">
                <LabelRow>Email address <span className="req">*</span></LabelRow>
                <InputShell>
                  <FieldIco className="field-ico"><IconMail /></FieldIco>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="jane.doe@example.com"
                    autoComplete="email"
                    inputMode="email"
                    $withIcon
                    $error={errors.email}
                  />
                </InputShell>
                {errors.email && <ErrText><IconAlertTri />{errors.email}</ErrText>}
              </Field>

              <Field $delay="160ms">
                <LabelRow>Phone number <span className="req">*</span></LabelRow>
                <InputShell>
                  <FieldIco className="field-ico"><IconPhone /></FieldIco>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+233 XX XXX XXXX"
                    autoComplete="tel"
                    inputMode="tel"
                    $withIcon
                    $error={errors.phone}
                  />
                </InputShell>
                {errors.phone && <ErrText><IconAlertTri />{errors.phone}</ErrText>}
              </Field>

              <Grid2>
                <Field $delay="200ms">
                  <LabelRow>
                    Password <span className="req">*</span>
                    <span className="hint">min. 8 characters</span>
                  </LabelRow>
                  <InputShell>
                    <FieldIco className="field-ico"><IconLock /></FieldIco>
                    <Input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      $withIcon
                      $error={errors.password}
                    />
                  </InputShell>
                  {errors.password && <ErrText><IconAlertTri />{errors.password}</ErrText>}
                </Field>

                <Field $delay="240ms">
                  <LabelRow>Confirm password <span className="req">*</span></LabelRow>
                  <InputShell>
                    <FieldIco className="field-ico"><IconLock /></FieldIco>
                    <Input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Re-enter password"
                      autoComplete="new-password"
                      $withIcon
                      $error={errors.confirmPassword}
                    />
                  </InputShell>
                  {errors.confirmPassword && <ErrText><IconAlertTri />{errors.confirmPassword}</ErrText>}
                </Field>
              </Grid2>

              <Actions>
                <BtnPrimary type="button" onClick={handleNext}>
                  <span>Continue</span>
                  <IconArrow />
                </BtnPrimary>
              </Actions>
            </>
          )}

          {/* ═════ STEP 2 ═════ */}
          {currentStep === 2 && (
            <>
              <CardHeader>
                <span className="pill">Step 02 — Credentials</span>
                <h2>Professional credentials.</h2>
                <p>Tell us about your license and where you currently practice.</p>
              </CardHeader>

              <Field $delay="40ms">
                <LabelRow>License type <span className="req">*</span></LabelRow>
                <InputShell>
                  <FieldIco className="field-ico"><IconBadge /></FieldIco>
                  <Select
                    name="licenseType"
                    value={formData.licenseType}
                    onChange={handleInputChange}
                    $withIcon
                    $error={errors.licenseType}
                  >
                    <option value="">Select license type</option>
                    {LICENSE_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </Select>
                </InputShell>
                {errors.licenseType && <ErrText><IconAlertTri />{errors.licenseType}</ErrText>}

                {formData.licenseType === 'Other' && (
                  <div style={{ marginTop: 10 }}>
                    <InputShell>
                      <FieldIco className="field-ico"><IconBadge /></FieldIco>
                      <Input
                        type="text"
                        name="licenseTypeOther"
                        value={formData.licenseTypeOther || ''}
                        onChange={handleInputChange}
                        placeholder="Please specify your license type"
                        $withIcon
                        $error={errors.licenseTypeOther}
                      />
                    </InputShell>
                    {errors.licenseTypeOther && <ErrText><IconAlertTri />{errors.licenseTypeOther}</ErrText>}
                  </div>
                )}
              </Field>

              <Grid2>
                <Field $delay="80ms">
                  <LabelRow>License number <span className="req">*</span></LabelRow>
                  <InputShell>
                    <FieldIco className="field-ico"><IconBadge /></FieldIco>
                    <Input
                      name="licenseNumber"
                      value={formData.licenseNumber}
                      onChange={handleInputChange}
                      placeholder="e.g., PSY-12345"
                      $withIcon
                      $error={errors.licenseNumber}
                    />
                  </InputShell>
                  {errors.licenseNumber && <ErrText><IconAlertTri />{errors.licenseNumber}</ErrText>}
                </Field>

                <Field $delay="120ms">
                  <LabelRow>Country <span className="req">*</span></LabelRow>
                  <InputShell>
                    <FieldIco className="field-ico"><IconGlobe /></FieldIco>
                    <Select
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      disabled={countriesLoading}
                      $withIcon
                      $error={errors.country}
                    >
                      <option value="">
                        {countriesLoading ? 'Loading countries…' : 'Select your country'}
                      </option>
                      {countries.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </Select>
                  </InputShell>
                  {errors.country && <ErrText><IconAlertTri />{errors.country}</ErrText>}
                </Field>
              </Grid2>

              <Field $delay="160ms">
                <LabelRow>License state / region <span className="req">*</span></LabelRow>
                <InputShell>
                  <FieldIco className="field-ico"><IconMapPin /></FieldIco>
                  <Input
                    name="licenseState"
                    value={formData.licenseState}
                    onChange={handleInputChange}
                    placeholder="e.g., Greater Accra"
                    $withIcon
                    $error={errors.licenseState}
                  />
                </InputShell>
                {errors.licenseState && <ErrText><IconAlertTri />{errors.licenseState}</ErrText>}
              </Field>

              <Grid2>
                <Field $delay="200ms">
                  <LabelRow>Expiry date <span className="req">*</span></LabelRow>
                  <InputShell>
                    <FieldIco className="field-ico"><IconCalendar /></FieldIco>
                    <Input
                      type="date"
                      name="licenseExpiry"
                      value={formData.licenseExpiry}
                      onChange={handleInputChange}
                      $withIcon
                      $error={errors.licenseExpiry}
                    />
                  </InputShell>
                  {errors.licenseExpiry && <ErrText><IconAlertTri />{errors.licenseExpiry}</ErrText>}
                </Field>

                <Field $delay="220ms">
                  <LabelRow>Highest Educational Level <span className="req">*</span></LabelRow>
                  <InputShell>
                    <FieldIco className="field-ico"><IconBadge /></FieldIco>
                    <Select
                      name="highestEducationalLevel"
                      value={formData.highestEducationalLevel}
                      onChange={handleInputChange}
                      $withIcon
                      $error={errors.highestEducationalLevel}
                    >
                      <option value="">Select highest educational level</option>
                      {HIGHEST_EDUCATIONAL_LEVELS.map((level) => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </Select>
                  </InputShell>
                  {errors.highestEducationalLevel && <ErrText><IconAlertTri />{errors.highestEducationalLevel}</ErrText>}
                </Field>

                <Field $delay="240ms">
                  <LabelRow>Years of experience <span className="req">*</span></LabelRow>
                  <InputShell>
                    <FieldIco className="field-ico"><IconClock /></FieldIco>
                    <Input
                      type="number"
                      name="yearsOfExperience"
                      value={formData.yearsOfExperience}
                      onChange={handleInputChange}
                      placeholder="e.g., 5"
                      min="0"
                      max="60"
                      inputMode="numeric"
                      $withIcon
                      $error={errors.yearsOfExperience}
                    />
                  </InputShell>
                  {errors.yearsOfExperience && <ErrText><IconAlertTri />{errors.yearsOfExperience}</ErrText>}
                </Field>
              </Grid2>

              <Field $delay="280ms">
                <LabelRow>Current workplace <span className="req">*</span></LabelRow>
                <InputShell>
                  <FieldIco className="field-ico"><IconBuilding /></FieldIco>
                  <Input
                    name="workplace"
                    value={formData.workplace}
                    onChange={handleInputChange}
                    placeholder="e.g., Korle Bu Teaching Hospital"
                    $withIcon
                    $error={errors.workplace}
                  />
                </InputShell>
                {errors.workplace && <ErrText><IconAlertTri />{errors.workplace}</ErrText>}
              </Field>

              <Field $delay="320ms">
                <LabelRow>Workplace address <span className="req">*</span></LabelRow>
                <TextArea
                  name="workplaceAddress"
                  value={formData.workplaceAddress}
                  onChange={handleInputChange}
                  placeholder="Full address of your workplace"
                  $error={errors.workplaceAddress}
                />
                {errors.workplaceAddress && <ErrText><IconAlertTri />{errors.workplaceAddress}</ErrText>}
              </Field>

              <Actions>
                <BtnGhost type="button" onClick={handleBack}>
                  <IconArrow />
                  <span>Back</span>
                </BtnGhost>
                <BtnPrimary type="button" onClick={handleNext}>
                  <span>Continue</span>
                  <IconArrow />
                </BtnPrimary>
              </Actions>
            </>
          )}

          {/* ═════ STEP 3 ═════ */}
          {currentStep === 3 && (
            <>
              <CardHeader>
                <span className="pill">Step 03 — Documents</span>
                <h2>Documents &amp; profile.</h2>
                <p>Add a profile photo, then optionally include a bio and supporting documents for verification.</p>
              </CardHeader>

              {/* Hidden file inputs */}
              <HiddenInput
                ref={cvInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleCvChange}
              />
              <HiddenInput
                ref={licenseInputRef}
                type="file"
                accept=".pdf,application/pdf,image/*"
                onChange={handleLicenseChange}
              />
              <HiddenInput
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
              />

              {/* Photo */}
              <Field $delay="40ms">
                <LabelRow>
                  Profile photo <span className="req">*</span>
                  <span className="hint">image, max 2MB</span>
                </LabelRow>
                <DropZone
                  $hasFile={!!photoFile}
                  $error={errors.photoFile}
                  onClick={() => photoInputRef.current?.click()}
                >
                  <DropIcon><IconCamera /></DropIcon>
                  <DropTitle>{photoFile ? 'Photo uploaded' : 'Tap to upload your photo'}</DropTitle>
                  <DropSub>{photoFile ? 'Tap again to replace' : 'Professional headshot recommended'}</DropSub>
                  {photoPreview && (
                    <PhotoPreview>
                      <img src={photoPreview} alt="Preview" />
                    </PhotoPreview>
                  )}
                  {photoFile && (
                    <FileBadge>
                      <span className="ico"><IconCamera /></span>
                      <span className="meta">
                        <span className="n">{photoFile.name}</span>
                        <span className="s">{fmtSize(photoFile.size)}</span>
                      </span>
                      <RemoveBtn onClick={removePhoto} type="button">
                        <IconX /> Remove
                      </RemoveBtn>
                    </FileBadge>
                  )}
                </DropZone>
                {errors.photoFile && <ErrText><IconAlertTri />{errors.photoFile}</ErrText>}
              </Field>

              {/* Bio */}
              <Field $delay="80ms">
                <LabelRow>
                  Professional bio
                  <span className="hint">optional</span>
                </LabelRow>
                <TextArea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  placeholder="Optional: tell us about your background, therapeutic approach, and areas of expertise…"
                  $error={errors.bio}
                />
                {errors.bio && <ErrText><IconAlertTri />{errors.bio}</ErrText>}
              </Field>

              <Divider><span>Optional document uploads</span></Divider>

              {/* CV */}
              <Field $delay="120ms">
                <LabelRow>
                  CV
                  <span className="hint">optional PDF, max 5MB</span>
                </LabelRow>
                <DropZone
                  $hasFile={!!cvFile}
                  $error={errors.cvFile}
                  onClick={() => cvInputRef.current?.click()}
                >
                  <DropIcon><IconDocument /></DropIcon>
                  <DropTitle>{cvFile ? 'CV uploaded' : 'Tap to upload your CV (optional)'}</DropTitle>
                  <DropSub>{cvFile ? 'Tap again to replace' : 'PDF format only'}</DropSub>
                  {cvFile && (
                    <FileBadge>
                      <span className="ico"><IconDocument /></span>
                      <span className="meta">
                        <span className="n">{cvFile.name}</span>
                        <span className="s">{fmtSize(cvFile.size)}</span>
                      </span>
                      <RemoveBtn onClick={removeCv} type="button">
                        <IconX /> Remove
                      </RemoveBtn>
                    </FileBadge>
                  )}
                </DropZone>
                {errors.cvFile && <ErrText><IconAlertTri />{errors.cvFile}</ErrText>}
              </Field>

              {/* License */}
              <Field $delay="160ms">
                <LabelRow>
                  License document
                  <span className="hint">optional PDF or image, max 5MB</span>
                </LabelRow>
                <DropZone
                  $hasFile={!!licenseFile}
                  $error={errors.licenseFile}
                  onClick={() => licenseInputRef.current?.click()}
                >
                  <DropIcon><IconId /></DropIcon>
                  <DropTitle>{licenseFile ? 'License uploaded' : 'Tap to upload your license (optional)'}</DropTitle>
                  <DropSub>{licenseFile ? 'Tap again to replace' : 'PDF, JPG, or PNG'}</DropSub>
                  {licenseFile && (
                    <FileBadge>
                      <span className="ico"><IconId /></span>
                      <span className="meta">
                        <span className="n">{licenseFile.name}</span>
                        <span className="s">{fmtSize(licenseFile.size)}</span>
                      </span>
                      <RemoveBtn onClick={removeLicense} type="button">
                        <IconX /> Remove
                      </RemoveBtn>
                    </FileBadge>
                  )}
                </DropZone>
                {errors.licenseFile && <ErrText><IconAlertTri />{errors.licenseFile}</ErrText>}
              </Field>

              <Callout>
                <span className="ico"><IconShield /></span>
                <div className="body">
                  <strong>Almost done</strong>
                  Your application will be reviewed by our admin team within 1–2 business days. You'll receive an email notification once approved.
                </div>
              </Callout>

              {submissionNotice && (
                <SubmissionNotice $type={submissionNotice.type} role="status" aria-live="polite">
                  <span className="ico">{submissionNotice.type === 'error' ? <IconAlertTri /> : <IconLoader />}</span>
                  <div className="body">
                    <strong>{submissionNotice.title}</strong>
                    {submissionNotice.message}
                    {uploadProgress !== null && ` Upload progress: ${uploadProgress}%.`}
                  </div>
                </SubmissionNotice>
              )}

              <Actions>
                <BtnGhost type="button" onClick={handleBack} disabled={loading}>
                  <IconArrow />
                  <span>Back</span>
                </BtnGhost>
                <BtnPrimary type="button" onClick={handleSubmit} disabled={loading} $loading={loading}>
                  {loading ? (
                    <>
                      <IconLoader />
                      <span>{uploadProgress !== null ? `Uploading ${uploadProgress}%…` : 'Submitting…'}</span>
                    </>
                  ) : (
                    <>
                      <span>Submit application</span>
                      <IconArrow />
                    </>
                  )}
                </BtnPrimary>
              </Actions>
            </>
          )}

          <CardFoot>
            Already have an account? <Link to="/login">Sign in here</Link>
          </CardFoot>
        </Card>
      </Shell>
    </Page>
  );
};

export default Register;
